import { inject, provide } from "vue";
import type { InjectionKey } from "vue";
import { useBreakpoint } from "@polargold/pg-frontend-vue";

type AppBreakpoint = ReturnType<typeof useBreakpoint>;

const APP_BREAKPOINT_KEY: InjectionKey<AppBreakpoint> = Symbol("appBreakpoint");

/**
 * Call once, from the app root (`App.vue`), to create a single shared `useBreakpoint()`
 * instance for the whole app. Replaces 6 independent `useBreakpoint()` call sites (each with
 * its own `resize` listener) that existed before this app migrated off `luv-ui-tools` - see
 * `useAppBreakpoint()` for how the rest of the app reads it.
 *
 * Provided from the app root rather than a Pinia store: this is ephemeral, derived UI state
 * (not something to persist or share outside the component tree), and provide/inject ties its
 * `resize` listener's lifecycle to the app root's own mount/unmount instead of whichever
 * component happened to call `useBreakpoint()` first.
 */
export const provideAppBreakpoint = (): void => {
    provide(APP_BREAKPOINT_KEY, useBreakpoint());
};

/**
 * Reads the shared breakpoint instance `provideAppBreakpoint()` set up at the app root.
 *
 * @throws if called before `provideAppBreakpoint()` has run (i.e. outside the app's component tree).
 */
export const useAppBreakpoint = (): AppBreakpoint => {
    const breakpoint = inject(APP_BREAKPOINT_KEY);

    if (!breakpoint) {
        throw new Error(
            "useAppBreakpoint() was called before provideAppBreakpoint() - make sure App.vue calls provideAppBreakpoint() in its own setup().",
        );
    }

    return breakpoint;
};
